/**
 * Workflow Mappers
 * Convert between frontend UI types and backend API DTOs
 *
 * Frontend Types (React Flow):
 * - WorkflowNode (from src/types/node.types.ts)
 * - WorkflowConnection (from src/types/edge.types.ts)
 *
 * Backend DTOs (API):
 * - WorkflowNodeDto (from src/api/types/workflow.types.ts)
 * - WorkflowEdgeDto (from src/api/types/workflow.types.ts)
 */

import { WorkflowNodeDto, WorkflowEdgeDto } from '../types/workflow.types';
import {
  WorkflowNode,
  WorkflowNodeData,
  ProcessElementData,
  ProcessElementPort,
  ProcessElementConfiguration,
  ConnectorConfiguration,
  WorkflowNodeRuntime,
  NodeState,
  NodeType,
} from '@flow-studio/types';
import { WorkflowConnection } from '@flow-studio/types';
import { NodeTypeApiClient } from '../clients/nodeTypeApiClient';

/**
 * Map frontend WorkflowNode to backend WorkflowNodeDto with ProcessElementTypeID lookup
 *
 * This is the main function to use when saving workflows. It automatically looks up
 * the ProcessElementTypeID from the backend API using the portable node type code.
 *
 * ARCHITECTURE:
 * - Frontend identifies nodes by CODE (portable, stable across databases)
 * - Code is looked up to get the database-specific ProcessElementTypeID
 * - ProcessElementTypeID is saved with the node for database storage
 * - ExecutorRegistry uses CODE (not ID) for executor selection at runtime
 * - This design ensures portability: same code, different ID in different databases
 *
 * @param node - Frontend node (React Flow format)
 * @param versionID - ProcessThreadVersion ID
 * @returns Backend DTO ready for API with correct ProcessElementTypeID
 * @throws Error if the node type code is not found in the database
 */
export async function mapNodeToDtoWithTypeResolution(
  node: WorkflowNode,
  versionID: number,
  edges: WorkflowConnection[] = []
): Promise<WorkflowNodeDto> {
  const pe = node.data.processElement as any;

  // ✅ Resolve the portable node type CODE — priority order:
  //   1. configuration.designer.ui.nodeType — the node code stamped by mapDtoToNode / NodeFactoryService
  //   2. node.type only when it's NOT a shape name (backward-compat for old saves)
  //   3. Empty string → fall through to ID-based path below
  const storedCode = (pe.configuration?.designer?.ui as any)?.nodeType as string | undefined;
  const rawCode = (storedCode && !SHAPE_NAMES.has(storedCode))
    ? storedCode
    : (node.type && !SHAPE_NAMES.has(node.type) ? node.type : '') ?? '';
  // Normalize legacy codes that were corrupted by earlier name-recovery logic
  const CODE_ALIASES: Record<string, string> = { 'ai': 'ai-agent' };
  const nodeTypeCode = CODE_ALIASES[rawCode] ?? rawCode;

  // ✅ If we already have the DB ID (loaded/saved node) and no type code, skip the API lookup —
  //    mapNodeToDto will use pe.processElementTypeID directly.
  if (!nodeTypeCode) {
    if (pe.processElementTypeID) {
      return mapNodeToDto(node, versionID, pe.processElementTypeID, edges);
    }
    // New node with completely missing type info — warn but don't throw
    console.warn(`Cannot determine node type code for node ${node.id} — saving with fallback typeID. Re-add the node to fix.`);
    return mapNodeToDto(node, versionID, 1, edges);
  }

  // ✅ Look up the ProcessElementTypeID from the backend using the CODE
  // The ID is environment-specific and may differ in different databases
  const nodeTypeApiClient = new NodeTypeApiClient();
  let processElementTypeID = pe.processElementTypeID ?? 0;

  try {
    const nodeType = await nodeTypeApiClient.getNodeType(nodeTypeCode);
    if (nodeType.id) {
      processElementTypeID = nodeType.id;
    }
  } catch (error) {
    const displayName = pe.displayName || nodeTypeCode;
    console.error(
      `[WorkflowSave] Cannot resolve ProcessElementTypeID for node "${displayName}" (code: "${nodeTypeCode}", nodeId: "${node.id}").\n` +
      `  Root cause: The node type code "${nodeTypeCode}" does not exist in the process-element-types table.\n` +
      `  Fix: Register "${nodeTypeCode}" via POST /api/v1/process/process-element-types or seed it in the database.\n` +
      `  Save will proceed with ProcessElementTypeID=0 and will be rejected by the backend (400).`,
      error
    );
  }

  return mapNodeToDto(node, versionID, processElementTypeID, edges);
}

/**
 * Map frontend WorkflowNode to backend WorkflowNodeDto
 *
 * NOTE: For new nodes, prefer using mapNodeToDtoWithTypeResolution() which automatically
 * looks up the correct ProcessElementTypeID. Use this function only for:
 * - Existing nodes that already have a processElementTypeID set
 * - Bulk operations where you want to control the mapping
 *
 * @param node - Frontend node (React Flow format)
 * @param versionID - ProcessThreadVersion ID
 * @param processElementTypeID - Optional ProcessElementTypeID (defaults to 1)
 * @returns Backend DTO ready for API
 */
export function mapNodeToDto(
  node: WorkflowNode,
  versionID: number,
  processElementTypeID: number = 0,
  edges: WorkflowConnection[] = []
): WorkflowNodeDto {
  const pe = node.data.processElement as any;

  // Extract template information if node was created from template
  const template = node.templateReference ? {
    code: node.templateReference.code,
    displayName: node.templateReference.displayName,
    extensionID: node.templateReference.extensionId,
    category: node.templateReference.category,
    version: node.templateReference.version,
    iconUrl: node.templateReference.iconUrl,
    color: node.templateReference.color,
    description: node.templateReference.description,
    isDeprecated: node.templateReference.isDeprecated ?? false,
    createdFromTemplateAt: new Date().toISOString(),
  } : undefined;

  // Build the connector payload whenever a connector object is present — including new nodes
  // (no connectorID yet). The backend CreateConnectorForNodeAsync uses connector.Configuration
  // to seed the new Connector record on first save.
  const connectorCfg = pe.connector?.configuration ?? pe.connector?.Configuration;
  const connectorPayload = pe.connector
    ? {
        ...pe.connector,
        configuration: connectorCfg
          ? (typeof connectorCfg === 'string' ? connectorCfg : JSON.stringify(connectorCfg))
          : null,
      }
    : undefined;

  // Calculate isSatellite: a node is satellite if:
  // 1. It has satellite ports (isSatellite=true) AND
  // 2. At least one satellite port is the TARGET of a connection (receives input)
  const allPorts = [...(pe.ports ?? [])];
  const satellitePorts = allPorts.filter((p: any) => p.isSatellite === true);

  // Satellite nodes are TARGETS of connections (they receive on satellite input ports)
  const hasActiveSatelliteConnection = satellitePorts.some((satellitePort: any) =>
    edges.some((edge: any) =>
      edge.target === node.id && (edge.targetHandle === satellitePort.portKey ||
      (!edge.targetHandle && satellitePort.portKey === 'main'))
    )
  );

  const isSatellite = satellitePorts.length > 0 && hasActiveSatelliteConnection;

  // DEBUG: Log satellite detection
  if (satellitePorts.length > 0) {
    const incomingEdges = edges.filter((e: any) => e.target === node.id);
    console.log(`[DEBUG mapNodeToDto] Node ${node.id} (${pe.displayName}): ${satellitePorts.length} satellite ports, ${incomingEdges.length} incoming edges, isSatellite=${isSatellite}`);
  }

  return {
    // Identity
    processElementID: pe.processElementID ?? node.processElementId,
    processThreadVersionID: versionID,
    processElementKey: node.id,

    // Core Properties
    name: pe.name || node.id,
    displayName: pe.displayName,
    extensionID: pe.extensionID ?? node.templateReference?.extensionId ?? 1,
    template,
    description: pe.description,
    processElementTypeID: pe.processElementTypeID ?? processElementTypeID,

    // Position & Layout
    positionX: Math.round(node.position.x),
    positionY: Math.round(node.position.y),
    zIndex: node.zIndex ?? 0,

    // UI State
    isSelected: node.selected ?? false,
    isLocked: pe.isLocked ?? false,

    // Behavior Flags
    isTrigger: pe.isTrigger,
    isSatellite: isSatellite,
    isDisabled: pe.isDisabled,
    continueOnFail: pe.continueOnFail,
    alwaysOutputData: pe.alwaysOutputData,
    retryOnFail: pe.retryOnFail,

    // Configuration
    maxRetries: pe.maxRetries,
    retryWaitTime: pe.retryWaitTime,
    timeout: pe.timeout,
    pinnedData: pe.pinnedData,

    // Connector
    connectorID: pe.connectorID,
    executionModeID: undefined,
    processElementGroupID: undefined,

    // Notes
    notes: pe.notes,

    // Configuration JSON — persists designer UI and all port definitions
    // Both input and output ports stored in nodeFormValues with ALL properties preserved
    // Form can edit subset (usage, port, label, isDefault) while preserving all other properties
    configuration: (() => {
      const cfg = pe.configuration;
      const inputPorts = pe.ports?.filter((p: any) => p.portType === 'input') ?? [];
      const outputPorts = pe.ports?.filter((p: any) => p.portType === 'output') ?? [];

      // Store complete port objects with all properties
      // Form edits: usage, port, label, isDefault
      // Preserved: portKey, portType, portIndex, isRequired, isMultiple, isErrorPort, isSatellite, position, handleStyle, color, etc.
      const inputPortsFormData = inputPorts.map((p: any) => ({
        ...p,
        usage: p.portKey,
        port: p.portKey,
        label: p.label ?? p.portKey,
        isDefault: p.isMainPort ?? false,
      }));

      const outputPortsFormData = outputPorts.map((p: any) => ({
        ...p,
        usage: p.portKey,
        port: p.portKey,
        label: p.label ?? p.portKey,
        isDefault: p.isMainPort ?? false,
      }));

      const cleanCfg = cfg?.designer?.ui?.style
        ? {
            ...cfg,
            designer: {
              ...cfg.designer,
              ui: (() => {
                const { width: _w, height: _h, ...cleanStyle } = cfg.designer.ui.style as any;
                return { ...cfg.designer.ui, style: cleanStyle };
              })(),
            },
          }
        : cfg;

      return JSON.stringify({
        ...cleanCfg,
        nodeFormValues: {
          ...cleanCfg?.nodeFormValues,
          inputPorts: inputPortsFormData.length > 0 ? inputPortsFormData : undefined,
          outputPorts: outputPortsFormData.length > 0 ? outputPortsFormData : undefined,
        },
      });
    })(),

    // Nested Connector with its own stringified Configuration
    connector: connectorPayload,
  };
}

/**
 * Map frontend WorkflowConnection to backend WorkflowEdgeDto
 *
 * @param edge - Frontend edge (React Flow format)
 * @param versionID - ProcessThreadVersion ID
 * @returns Backend DTO ready for API
 *
 * CRITICAL: Preserve sourceHandle and targetHandle exactly.
 * These map to sourcePortKey and targetPortKey in the DTO.
 * For AI Agent nodes, targetHandle can be "main", "base-node", "team-member", "llm", "memory", or "tool".
 */
export function mapEdgeToDto(edge: WorkflowConnection, versionID: number): WorkflowEdgeDto {
  const dto = {
    // Identity
    connectionID: edge.connectionId, // Database ID (undefined for new edges)
    processThreadVersionID: versionID,

    // References to nodes
    sourceElementKey: edge.source,
    targetElementKey: edge.target,
    sourceProcessElementID: undefined, // Backend will resolve from sourceElementKey
    targetProcessElementID: undefined, // Backend will resolve from targetElementKey

    // Connection Properties
    connectionTypeID: 1, // Default: Standard connection
    sourcePortKey: edge.sourceHandle || 'main',
    targetPortKey: edge.targetHandle || 'main',
    // Edge Styling
    edgeTypeID: undefined,
    edgeStyleID: undefined,
    label: edge.label,

    // Conditional Logic
    conditionExpression: edge.data?.condition,
    isConditional: edge.data?.isConditional || false,

    // Display
    displayOrder: edge.data?.displayOrder || 9999,
  };

  return dto;
}

const SHAPE_NAMES = new Set(['circle', 'diamond', 'custom', 'ai-function-node', 'subflow', 'default']);

/**
 * One configured output-port entry, matching the backend's OutputPortMapping shape
 * (BizFirst.Ai.ProcessEngine.Domain OutputPortUsageInfo: usage/port/label/isDefault),
 * as stored in any node's Configuration JSON under the "OutputPortMapping" key
 * (e.g. Switch's SwitchNodeSettings, but the shape is shared by every
 * BaseNodeExecutorSettings-derived executor's OutputMapping).
 */
interface OutputPortMappingEntry {
  usage?: string;
  port?: string;
  label?: string;
  isDefault?: boolean;
}

/**
 * Step 1 — Loading.
 * Single responsibility: read raw output-port candidates for a node from ONE source
 * at a time. Never merges sources, never dedupes, never decides precedence — that is
 * OutputPortManager's job. Each method here maps one input shape to the port shape
 * the designer canvas expects.
 */
class OutputPortLoader {
  /**
   * Ports explicitly configured by the user — e.g. a Switch node's "OutputPortMapping"
   * array in its Configuration JSON (BizFirst.Ai.ProcessEngine.Domain OutputPortUsageInfo:
   * usage/port/label/isDefault). These are high priority: see OutputPortManager.addConfigured.
   */
  static loadConfigured(mapping?: OutputPortMappingEntry[]): Array<any> {
    if (!Array.isArray(mapping) || mapping.length === 0) return [];
    return mapping.map((entry, i) => ({
      portKey: entry.port || entry.usage,
      label: entry.label || entry.usage || entry.port,
      portType: 'output',
      position: 'right',
      handleStyle: 'circle',
      color: entry.isDefault ? '#ef4444' : (entry.usage === 'error' ? '#dc2626' : '#22c55e'),
      isMainPort: !!entry.isDefault,
      offsetY: (i - (mapping.length - 1) / 2) * 34,
    }));
  }

  /** Generic fallback scaffold for a Switch node with nothing configured yet. */
  static loadSwitchDefaults(): Array<any> {
    return [
      { portKey: 'case_1', label: 'Case 1', portType: 'output', position: 'right', handleStyle: 'circle', color: '#22c55e', isMainPort: true, offsetY: -34 },
      { portKey: 'case_2', label: 'Case 2', portType: 'output', position: 'right', handleStyle: 'circle', color: '#f59e0b', isMainPort: false, offsetY: 0 },
      { portKey: 'default', label: 'Default', portType: 'output', position: 'right', handleStyle: 'circle', color: '#ef4444', isMainPort: false, offsetY: 34 },
    ];
  }
}

/**
 * Step 2 — Managing.
 * Single responsibility: own the final, de-duplicated output-port set for a node.
 *
 * Rule enforced here (and ONLY here): a port added via addConfigured() came from the
 * user's own configuration and is high priority — it is NEVER rejected and NEVER
 * overwritten by a later add. addDefaults() may only fill in portKeys that are still
 * unclaimed; any default whose portKey collides with a configured (or already-added
 * default) port is rejected and logged, never silently overwriting it.
 */
class OutputPortManager {
  private readonly ports = new Map<string, any>();
  private readonly configuredKeys = new Set<string>();

  /** Register user-configured ports. Unconditional — none of these are ever rejected. */
  addConfigured(ports: Array<any>): this {
    for (const port of ports) {
      if (!port?.portKey) continue;
      this.ports.set(port.portKey, port);
      this.configuredKeys.add(port.portKey);
    }
    return this;
  }

  /** Register fallback/default ports. Skipped (rejected) wherever the portKey is already claimed. */
  addDefaults(ports: Array<any>): this {
    for (const port of ports) {
      if (!port?.portKey) continue;
      if (this.ports.has(port.portKey)) {
        if (this.configuredKeys.has(port.portKey)) {
          console.warn(
            `[OutputPortManager] Rejected default port "${port.portKey}" — a user-configured port already owns this key.`
          );
        }
        continue; // reject duplicate — never overwrite a port already registered
      }
      this.ports.set(port.portKey, port);
    }
    return this;
  }

  get list(): Array<any> {
    return Array.from(this.ports.values());
  }
}

/**
 * Map a node category (from the API/templates table) to a React Flow node shape.
 * Code must never know about specific node codes — shape is driven by category only.
 * If category is absent or unknown, 'custom' is the universal fallback.
 */
function categoryToShape(category?: string): string {
  switch (category) {
    case 'trigger':  return 'circle';
    case 'logic':    return 'diamond';
    case 'ai-agent': return 'ai-agent'; // multi-port AI Agent visual
    case 'workflow': return 'subflow';  // sub-workflow visual
    default:         return 'custom';
  }
}

/**
 * Node-type → default port scaffold. Pure lookup: knows nothing about any specific
 * node instance's configuration. See configurePortsForNodeType for the
 * OutputPortMapping override layered on top of these defaults.
 *
 * @param nodeType - The type of the node (e.g., 'manual-trigger', 'http-request', 'if-condition', 'ai-agent')
 * @param isTrigger - Whether the node is a trigger node
 * @returns Object with inputPorts and outputPorts arrays
 *
 * CRITICAL: AI Agent nodes have specialized input ports (main, base-node, team-member, llm, memory, tool)
 * These must be properly configured so that saved connections can be restored correctly.
 */
function computeDefaultPortsForNodeType(nodeType: string, isTrigger: boolean): {
  inputPorts: Array<any>;
  outputPorts: Array<any>;
} {
  const inputPorts: Array<any> = [];
  const outputPorts: Array<any> = [];

  // Trigger nodes (no inputs, only outputs)
  if (isTrigger || nodeType.includes('trigger')) {
    outputPorts.push({
      portKey: 'main',
      label: 'Main Output',
      portType: 'output',
      position: 'right',
      handleStyle: 'arrow',
      color: '#10b981',
      isMainPort: true,
    });
    return { inputPorts, outputPorts };
  }

  // AI Agent nodes (special multi-port node)
  if (nodeType === 'ai-agent') {
    // Input ports
    inputPorts.push(
      { portKey: 'main',         label: 'Main Input',   portType: 'input',  position: 'left',   handleStyle: 'arrow',   color: '#3b82f6', isMainPort: true  },
      { portKey: 'base-node',    label: 'Base Agent',  portType: 'input',  position: 'bottom', handleStyle: 'circle',  color: '#06b6d4', isMainPort: false, isSatellite: true },
      { portKey: 'team-member',  label: 'Sub Agent',   portType: 'input',  position: 'left',   handleStyle: 'circle',  color: '#ec4899', isMainPort: false, isSatellite: true, labelOffsetX: -5, labelOffsetY: -3 },
      { portKey: 'llm',          label: 'LLM',          portType: 'input',  position: 'top',    handleStyle: 'diamond', color: '#ef4444', isMainPort: false, isSatellite: true },
      { portKey: 'memory',       label: 'Memory',       portType: 'input',  position: 'top',    handleStyle: 'diamond', color: '#8b5cf6', isMainPort: false, isSatellite: true, offsetX: -5 },
      { portKey: 'tool',         label: 'Tools',        portType: 'input',  position: 'bottom', handleStyle: 'diamond', color: '#f59e0b', isMainPort: false, isSatellite: true }
    );

    // Output ports
    outputPorts.push(
      { portKey: 'main',          label: 'Success',       portType: 'output', position: 'right', handleStyle: 'arrow', color: '#22c55e', isMainPort: true  },
      { portKey: 'extends',     label: 'Extends',          portType: 'output', position: 'left', handleStyle: 'diamond', color: '#14b8a6', isMainPort: false },
      { portKey: 'team-leader', label: 'Sub Agents',       portType: 'output', position: 'bottom', handleStyle: 'square',  color: '#f97316', isMainPort: false }
    );
    return { inputPorts, outputPorts };
  }

  // HIL approval node — named output ports
  if (nodeType === 'approval') {
    inputPorts.push({ portKey: 'main', label: 'Main Input', portType: 'input', position: 'left', handleStyle: 'arrow', color: '#3b82f6', isMainPort: true });
    outputPorts.push(
      { portKey: 'waiting',   label: 'Waiting',   portType: 'output', position: 'top',    handleStyle: 'circle', color: '#94a3b8', isMainPort: false, isErrorPort: false, offsetX: -50 },
      { portKey: 'cancelled', label: 'Cancelled', portType: 'output', position: 'top',    handleStyle: 'circle', color: '#6b7280', isMainPort: false, isErrorPort: false, offsetX:  50 },
      { portKey: 'approved',  label: 'Approved',  portType: 'output', position: 'right',  handleStyle: 'circle', color: '#22c55e', isMainPort: true,  isErrorPort: false, offsetY: -20 },
      { portKey: 'rejected',  label: 'Rejected',  portType: 'output', position: 'right',  handleStyle: 'circle', color: '#ef4444', isMainPort: false, isErrorPort: false, offsetY:  20 },
      { portKey: 'escalated', label: 'Escalated', portType: 'output', position: 'right',  handleStyle: 'circle', color: '#f97316', isMainPort: false, isErrorPort: false, offsetY:  60 },
      { portKey: 'abstained', label: 'Abstained', portType: 'output', position: 'right',  handleStyle: 'circle', color: '#a855f7', isMainPort: false, isErrorPort: false, offsetY: 100 },
      { portKey: 'error',     label: 'Error',     portType: 'output', position: 'bottom', handleStyle: 'circle', color: '#dc2626', isMainPort: false, isErrorPort: true,  offsetX:   0 }
    );
    return { inputPorts, outputPorts };
  }

  // Conditional nodes (one input, multiple outputs)
  if (nodeType === 'if-condition' || nodeType === 'filter') {
    inputPorts.push({
      portKey: 'main',
      label: 'Main Input',
      portType: 'input',
      position: 'left',
      handleStyle: 'arrow',
      color: '#3b82f6',
      isMainPort: true,
    });
    outputPorts.push({
      portKey: 'true',
      label: 'True',
      portType: 'output',
      position: 'right',
      handleStyle: 'circle',
      color: '#10b981',
      isMainPort: false,
    });
    outputPorts.push({
      portKey: 'false',
      label: 'False',
      portType: 'output',
      position: 'right',
      handleStyle: 'circle',
      color: '#ef4444',
      isMainPort: false,
    });
    return { inputPorts, outputPorts };
  }

  // Switch node — generic 2-case + default scaffold. Unlike If/Filter this is only
  // ever a starting point: a real Switch is expected to have a configured
  // OutputPortMapping, which configurePortsForNodeType() applies as a full
  // replacement (see its docs — this is the same generic mechanism every node
  // type gets, not something special-cased for Switch).
  if (nodeType === 'switch') {
    inputPorts.push({
      portKey: 'main',
      label: 'Main Input',
      portType: 'input',
      position: 'left',
      handleStyle: 'arrow',
      color: '#3b82f6',
      isMainPort: true,
    });
    outputPorts.push(...OutputPortLoader.loadSwitchDefaults());
    return { inputPorts, outputPorts };
  }

  // HIL Form Input node — submitted (main) + error port matching the node-library template.
  if (nodeType === 'hil-form') {
    inputPorts.push({ portKey: 'main', label: 'Main Input', portType: 'input', position: 'left', handleStyle: 'arrow', color: '#3b82f6', isMainPort: true });
    outputPorts.push(
      { portKey: 'submitted', label: 'Submitted', portType: 'output', position: 'right', handleStyle: 'circle', color: '#22c55e', isMainPort: true,  isErrorPort: false, offsetY: -20 },
      { portKey: 'error',     label: 'Error',     portType: 'output', position: 'right', handleStyle: 'circle', color: '#dc2626', isMainPort: false, isErrorPort: true,  offsetY: 20  },
    );
    return { inputPorts, outputPorts };
  }

  // HIL Chat Reply node — replied (main) + error port matching the node-library template.
  if (nodeType === 'chat-receive') {
    inputPorts.push({ portKey: 'main', label: 'Main Input', portType: 'input', position: 'left', handleStyle: 'arrow', color: '#3b82f6', isMainPort: true });
    outputPorts.push(
      { portKey: 'replied', label: 'Replied', portType: 'output', position: 'right', handleStyle: 'circle', color: '#22c55e', isMainPort: true,  isErrorPort: false, offsetY: -15 },
      { portKey: 'error',   label: 'Error',   portType: 'output', position: 'right', handleStyle: 'circle', color: '#dc2626', isMainPort: false, isErrorPort: true,  offsetY: 15  },
    );
    return { inputPorts, outputPorts };
  }

  // Action nodes with main/error output ports.
  // These executors fire OutputPortKey='main' on success and 'error' on failure.
  // Connections from these nodes must have SourcePortKey='main' (success) or 'error'.
  const successErrorNodes = new Set([
    'variable-assignment', 'json-transform', 'collection-operation',
    'code-execute', 'http-request', 'graphql-request', 'rest-api',
    'mysql-query', 'postgresql-query', 'sqlserver-query', 'mongodb-query', 'redis-query',
    'elasticsearch',
    'send-email',
    'email-gmail',
    'slack-message', 'slack', 'slack-message-send-wait', 'slack-message-send-ephemeral',
    'slack-message-update', 'slack-message-delete', 'slack-message-get-permalink', 'slack-message-search',
    'slack-channel-create', 'slack-channel-get', 'slack-channel-get-many', 'slack-channel-get-history',
    'slack-channel-get-members', 'slack-channel-get-replies', 'slack-channel-invite', 'slack-channel-join',
    'slack-channel-kick', 'slack-channel-leave', 'slack-channel-open', 'slack-channel-rename',
    'slack-channel-set-purpose', 'slack-channel-set-topic', 'slack-channel-archive', 'slack-channel-unarchive',
    'slack-channel-close', 'slack-file-upload', 'slack-file-get', 'slack-file-get-many',
    'slack-reaction-add', 'slack-reaction-get', 'slack-reaction-remove',
    'slack-user-get', 'slack-user-get-many', 'slack-user-get-profile', 'slack-user-get-status', 'slack-user-update-profile',
    'slack-usergroup-create', 'slack-usergroup-disable', 'slack-usergroup-enable', 'slack-usergroup-get-many',
    'slack-usergroup-get-users', 'slack-usergroup-add-users', 'slack-usergroup-update',
    'teams-message', 'discord-message', 'telegram-message', 'sms-send',
    'read-file', 'write-file', 'delete-file',
    'aws-s3', 's3-upload', 's3-download', 'google-drive', 'dropbox', 'azure-blob', 'gcs-upload',
    'stripe-payment', 'paypal-payment', 'delay', 'data-mapping',
    'function', 'ai-function', 'subflow', 'sub-workflow',
  ]);
  if (successErrorNodes.has(nodeType)) {
    inputPorts.push({
      portKey: 'main',
      label: 'Main Input',
      portType: 'input',
      position: 'left',
      handleStyle: 'arrow',
      color: '#3b82f6',
      isMainPort: true,
    });
    outputPorts.push({
      portKey: 'main',
      label: 'Success',
      portType: 'output',
      position: 'right',
      handleStyle: 'arrow',
      color: '#10b981',
      isMainPort: true,
    });
    outputPorts.push({
      portKey: 'error',
      label: 'Error',
      portType: 'output',
      position: 'right',
      handleStyle: 'circle',
      color: '#ef4444',
      isMainPort: false,
    });
    return { inputPorts, outputPorts };
  }

  // Regular action nodes (one input, one output) — generic/unknown node types
  inputPorts.push({
    portKey: 'main',
    label: 'Main Input',
    portType: 'input',
    position: 'left',
    handleStyle: 'arrow',
    color: '#3b82f6',
    isMainPort: true,
  });
  outputPorts.push({
    portKey: 'main',
    label: 'Main Output',
    portType: 'output',
    position: 'right',
    handleStyle: 'arrow',
    color: '#10b981',
    isMainPort: true,
  });

  return { inputPorts, outputPorts };
}

/**
 * Configure input and output ports for a node.
 *
 * Layering:
 *  1. computeDefaultPortsForNodeType() supplies the node-type scaffold — fixed named
 *     ports for types like If/Approval, or a generic starter scaffold for dynamic
 *     types like Switch.
 *  2. If the node's own Configuration JSON carries a populated "OutputPortMapping"
 *     array — the same shape every BaseNodeExecutorSettings-derived backend executor
 *     reads via its OutputMapping property (see BizFirst.Ai.ProcessEngine.Domain
 *     OutputPortUsageInfo: usage/port/label/isDefault) — that configured mapping is
 *     the authoritative, complete definition of the node's real output ports at
 *     runtime, and REPLACES the scaffold's outputPorts entirely (all-or-nothing: a
 *     partial merge would leave scaffold ports on the canvas that the executor can
 *     never actually route to — see OutputPortManager/OutputPortLoader above).
 *
 * This applies to every node type EXCEPT 'ai-agent': its 'extends'/'team-leader'
 * output ports represent sub-agent connection topology, not OutputPortKey execution
 * routing, so a configured mapping never replaces them.
 *
 * @param nodeType - The type of the node (e.g., 'manual-trigger', 'http-request', 'if-condition', 'ai-agent')
 * @param isTrigger - Whether the node is a trigger node
 * @param outputPortMapping - The node's configured OutputPortMapping, if any (from Configuration.OutputPortMapping)
 * @returns Object with inputPorts and outputPorts arrays
 */
function configurePortsForNodeType(
  nodeType: string,
  isTrigger: boolean,
  outputPortMapping?: OutputPortMappingEntry[]
): {
  inputPorts: Array<any>;
  outputPorts: Array<any>;
} {
  const { inputPorts, outputPorts } = computeDefaultPortsForNodeType(nodeType, isTrigger);

  if (nodeType === 'ai-agent') {
    return { inputPorts, outputPorts };
  }

  const configuredPorts = OutputPortLoader.loadConfigured(outputPortMapping);
  if (configuredPorts.length === 0) {
    return { inputPorts, outputPorts };
  }

  const manager = new OutputPortManager().addConfigured(configuredPorts);
  return { inputPorts, outputPorts: manager.list };
}

/**
 * Map backend WorkflowNodeDto to frontend WorkflowNode
 *
 * @param dto - Backend DTO from API
 * @param nodeType - React Flow node type (default: 'custom')
 * @param nodeTemplates - Optional array of node templates (for fallback if config is missing)
 * @returns Frontend node for React Flow
 */
export function mapDtoToNode(dto: WorkflowNodeDto, _nodeType?: string, nodeTemplates?: NodeType[]): WorkflowNode {
  // Parse configuration JSON
  let configuration: ProcessElementConfiguration = {};
  if (dto.configuration) {
    try {
      configuration = JSON.parse(dto.configuration) as ProcessElementConfiguration;
    } catch {
      // Ignore parse errors — keep empty config
    }
  }

  const ui = configuration?.designer?.ui ?? {};

  // ── Node code (stored in data, never used as React Flow node type) ─────────────
  // Guard: if stored nodeType is itself a shape name (old code bug), treat as missing
  let nodeCode = (!SHAPE_NAMES.has(ui.nodeType ?? '')) ? (ui.nodeType ?? '') : '';

  if (!nodeCode && dto.name) {
    // Recover code from timestamped name e.g. "webhook-trigger-1769438574341" → "webhook-trigger"
    const parts = dto.name.split('-');
    if (parts.length > 1) {
      parts.pop();
      nodeCode = parts.join('-');
    }
  }

  // Normalize legacy codes corrupted by prior name-recovery (e.g. 'ai' → 'ai-agent')
  const NODE_CODE_ALIASES: Record<string, string> = { 'ai': 'ai-agent' };
  if (NODE_CODE_ALIASES[nodeCode]) nodeCode = NODE_CODE_ALIASES[nodeCode];

  // ── Category (drives shape) ──────────────────────────────────────────────────
  const category = ui.category || dto.template?.category || (dto.isTrigger ? 'trigger' : '');

  // ── Shape (React Flow node type) — category-based, with one code-based exception ─
  // The AI Function node shares one code ("ai-function") across many functional categories
  // ("ai", "data", …), so its dedicated shape is keyed off the code rather than the category
  // (mirrors how the AI Agent node is effectively 1:1 code↔category).
  const extractedType = nodeCode === 'ai-function' ? 'ai-function-node' : categoryToShape(category);

  // Visual property fallback chain: config → legacy dto → template lookup
  if (!ui.color && !ui.iconUrl && nodeTemplates?.length) {
    const storedDtId = ui.dataTemplateID as number | undefined;
    const tmpl = storedDtId
      ? (nodeTemplates.find((t) => t.id === storedDtId) ?? nodeTemplates.find((t) => t.code === nodeCode))
      : nodeTemplates.find((t) => t.code === nodeCode);
    if (tmpl) {
      configuration = {
        ...configuration,
        designer: {
          ...configuration.designer,
          ui: {
            ...ui,
            color: ui.color ?? tmpl.color,
            iconUrl: ui.iconUrl ?? tmpl.iconUrl,
            category: ui.category ?? tmpl.category,
          },
        },
      };
    }
  }

  // Merge legacy flat color/iconUrl into configuration so getNodeDisplayProps always works
  if (!ui.color && dto.color) {
    configuration = {
      ...configuration,
      designer: { ...configuration.designer, ui: { ...configuration.designer?.ui, color: dto.color } },
    };
  }
  if (!ui.iconUrl && dto.iconUrl) {
    configuration = {
      ...configuration,
      designer: { ...configuration.designer, ui: { ...configuration.designer?.ui, iconUrl: dto.iconUrl } },
    };
  }

  // Stamp node code + category into config so save round-trips can recover the code
  if (nodeCode || category) {
    configuration = {
      ...configuration,
      designer: {
        ...configuration.designer,
        ui: {
          ...configuration.designer?.ui,
          ...(nodeCode ? { nodeType: nodeCode } : {}),
          ...(category ? { category } : {}),
        },
      },
    };
  }

  // Parse connector configuration
  let connectorConfig: ConnectorConfiguration = {};
  if (dto.connector?.configuration) {
    try {
      connectorConfig = JSON.parse(String(dto.connector.configuration));
    } catch {
      // Keep empty
    }
  }

  // Load ports from stored configuration (persisted when node was created)
  // Both input and output ports stored in nodeFormValues with all properties preserved
  // This ensures the workflow uses the original port definitions, not marketplace templates which can change
  const defaultInputPorts = (configuration?.nodeFormValues?.inputPorts as any[]) ?? [];
  const defaultOutputPorts = (configuration?.nodeFormValues?.outputPorts as any[]) ?? [];

  // Use stored ports if available; all properties are already preserved
  // Map from form format back to port format (usage→portKey, isDefault→isMainPort)
  const convertedInputPorts = defaultInputPorts.map((p: any) => ({
    ...p,
    portKey: p.port || p.portKey || p.usage,
    isMainPort: p.isDefault !== undefined ? p.isDefault : p.isMainPort,
    portType: 'input' as const,
  }));

  const convertedOutputPorts = defaultOutputPorts.map((p: any) => ({
    ...p,
    portKey: p.port || p.portKey || p.usage,
    isMainPort: p.isDefault !== undefined ? p.isDefault : p.isMainPort,
    portType: 'output' as const,
  }));

  // Use stored ports if available; fallback to hardcoded if missing (for legacy/new nodes)
  const rawPorts = (convertedInputPorts.length > 0 || convertedOutputPorts.length > 0)
    ? { inputPorts: convertedInputPorts, outputPorts: convertedOutputPorts }
    : configurePortsForNodeType(nodeCode || extractedType, dto.isTrigger);

  // If connector configuration contains custom output ports, use those as output ports.
  // This allows the Output Port Mapping Configuration form to drive the node's actual handles.
  // "outputPorts" is the key the live grid control actually saves under; "output_ports_grid"
  // is the key used by the reference form-schema docs — check both.
  let resolvedOutputPorts = rawPorts.outputPorts;
  const gridPorts = connectorConfig.outputPorts ?? connectorConfig.output_ports_grid;
  if (Array.isArray(gridPorts) && gridPorts.length > 0) {
    resolvedOutputPorts = gridPorts.map((gp: any) => ({
      portKey: gp.port || gp.usage,
      label: gp.label || gp.usage,
      portType: 'output',
      position: 'right',
      handleStyle: 'circle',
      color: (gp.isDefault === true || gp.isDefault === 'true') ? '#10b981' : '#6366f1',
      isMainPort: gp.isDefault === true || gp.isDefault === 'true',
    }));
  }

  const ports: ProcessElementPort[] = [
    ...rawPorts.inputPorts.map((p, i) => ({
      portKey: p.portKey,
      portType: 'input' as const,
      portIndex: i,
      label: p.label,
      displayName: p.label,
      isRequired: p.isRequired ?? false,
      isMultiple: p.isMultiple ?? true,
      isMainPort: p.isMainPort ?? false,
      isErrorPort: p.isErrorPort ?? false,
      isSatellite: p.isSatellite ?? false,
      satellite: p.satellite,
      color: p.color,
      position: p.position as ProcessElementPort['position'],
      handleStyle: p.handleStyle as ProcessElementPort['handleStyle'],
      offsetX: p.offsetX,
      offsetY: p.offsetY,
    })),
    ...resolvedOutputPorts.map((p: any, i: number) => ({
      portKey: p.portKey,
      portType: 'output' as const,
      portIndex: i,
      label: p.label,
      displayName: p.label,
      isRequired: p.isRequired ?? false,
      isMultiple: p.isMultiple ?? true,
      isMainPort: p.isMainPort ?? false,
      isErrorPort: p.isErrorPort ?? false,
      isSatellite: p.isSatellite ?? false,
      satellite: p.satellite,
      color: p.color,
      position: p.position as ProcessElementPort['position'],
      handleStyle: p.handleStyle as ProcessElementPort['handleStyle'],
      offsetX: p.offsetX,
      offsetY: p.offsetY,
    })),
  ];

  const processElement: ProcessElementData = {
    processElementID: dto.processElementID,
    processElementKey: dto.processElementKey,
    processThreadVersionID: dto.processThreadVersionID,
    processElementTypeID: dto.processElementTypeID,
    extensionID: dto.extensionID,
    name: dto.name,
    displayName: dto.displayName,
    description: dto.description,
    isTrigger: dto.isTrigger,
    isDisabled: dto.isDisabled,
    continueOnFail: dto.continueOnFail,
    alwaysOutputData: dto.alwaysOutputData,
    retryOnFail: dto.retryOnFail,
    maxRetries: dto.maxRetries,
    retryWaitTime: dto.retryWaitTime,
    timeout: dto.timeout,
    isLocked: dto.isLocked,
    notes: dto.notes,
    pinnedData: dto.pinnedData,
    connectorID: dto.connectorID,
    configuration,
    ports,
    connector: dto.connector
      ? { ...dto.connector, configuration: connectorConfig }
      : { configuration: {} },
    // Keep legacy flat fields for compatibility
    color: dto.color,
    iconUrl: dto.iconUrl,
  };

  const runtime: WorkflowNodeRuntime = {
    executionStatus: 'idle',
    collapsed: false,
  };

  const data: WorkflowNodeData = { processElement, runtime };

  const node: WorkflowNode = {
    id: dto.processElementKey,
    type: extractedType,
    position: { x: dto.positionX, y: dto.positionY },
    processElementId: dto.processElementID,
    state: (dto.processElementID ? 'existing' : 'new') as NodeState,
    selected: dto.isSelected,
    dragging: false,
    zIndex: dto.zIndex,
    data,
  };
  return node;
}

/**
 * Map backend WorkflowEdgeDto to frontend WorkflowConnection
 *
 * @param dto - Backend DTO from API
 * @param edgeType - React Flow edge type (default: 'default')
 * @returns Frontend edge for React Flow
 *
 * CRITICAL: Restore sourcePortKey and targetPortKey from DTO to sourceHandle and targetHandle.
 * These values are preserved during save, and must be restored exactly during load.
 * For AI Agent nodes, targetHandle can be "main", "base-node", "team-member", "llm", "memory", or "tool".
 */
export function mapDtoToEdge(
  dto: WorkflowEdgeDto,
  edgeType: 'default' | 'straight' | 'step' | 'smoothstep' | 'simplebezier' = 'default'
): WorkflowConnection | null {
  // Validate that source and target keys exist
  if (!dto.sourceElementKey || !dto.targetElementKey) {
    return null; // Skip this edge
  }

  const edge = {
    // React Flow required properties
    id: dto.connectionID?.toString() || `${dto.sourceElementKey}-${dto.targetElementKey}`,
    source: dto.sourceElementKey,
    target: dto.targetElementKey,
    sourceHandle: dto.sourcePortKey || 'main',
    targetHandle: dto.targetPortKey || 'main',
    type: edgeType,

    // Backend ID
    connectionId: dto.connectionID,

    // State
    state: dto.connectionID ? 'existing' : 'new',

    // Label
    label: dto.label,

    // Connection data
    data: {
      connectionType: 'standard', // TODO: Map from connectionTypeID
      isConditional: dto.isConditional,
      condition: dto.conditionExpression,
      displayOrder: dto.displayOrder,

      // Runtime
      lastExecutionData: undefined,
      executionCount: 0,
    },

    // Style
    style: {},
    animated: false,
    selected: false,
  };

  return edge as unknown as WorkflowConnection;
}

/**
 * Batch convert nodes to DTOs with ProcessElementTypeID lookup
 *
 * This is the recommended function to use when saving workflows as it resolves
 * the correct ProcessElementTypeID for each node.
 *
 * @param nodes - Array of frontend nodes
 * @param versionID - ProcessThreadVersion ID
 * @returns Promise resolving to array of backend DTOs with correct ProcessElementTypeID
 */
export async function mapNodesToDtosWithTypeResolution(
  nodes: WorkflowNode[],
  versionID: number,
  edges: WorkflowConnection[] = []
): Promise<WorkflowNodeDto[]> {
  return Promise.all(nodes.map((node) => mapNodeToDtoWithTypeResolution(node, versionID, edges)));
}

/**
 * Batch convert nodes to DTOs
 *
 * NOTE: This function does not resolve ProcessElementTypeID. For new nodes, use
 * mapNodesToDtosWithTypeResolution() instead.
 *
 * @param nodes - Array of frontend nodes
 * @param versionID - ProcessThreadVersion ID
 * @returns Array of backend DTOs (with ProcessElementTypeID defaulting to 1)
 */
export function mapNodesToDtos(nodes: WorkflowNode[], versionID: number): WorkflowNodeDto[] {
  return nodes.map((node) => mapNodeToDto(node, versionID));
}

/**
 * Batch convert edges to DTOs
 *
 * @param edges - Array of frontend edges
 * @param versionID - ProcessThreadVersion ID
 * @returns Array of backend DTOs
 */
export function mapEdgesToDtos(edges: WorkflowConnection[], versionID: number): WorkflowEdgeDto[] {
  return edges.map((edge) => mapEdgeToDto(edge, versionID));
}

/**
 * Batch convert DTOs to nodes
 *
 * @param dtos - Array of backend node DTOs
 * @param nodeType - React Flow node type
 * @param nodeTemplates - Optional array of node templates to restore visual properties
 * @returns Array of frontend nodes
 */
export function mapDtosToNodes(dtos: WorkflowNodeDto[], nodeType?: string, nodeTemplates?: Array<any>): WorkflowNode[] {
  if (!dtos) {
    return [];
  }

  if (!Array.isArray(dtos)) {
    return [];
  }

  return dtos.map((dto) => mapDtoToNode(dto, nodeType, nodeTemplates));
}

/**
 * Batch convert DTOs to edges
 *
 * @param dtos - Array of backend edge DTOs
 * @param edgeType - React Flow edge type
 * @returns Array of frontend edges
 */
export function mapDtosToEdges(dtos: WorkflowEdgeDto[], edgeType?: any): WorkflowConnection[] {
  if (!dtos) {
    return [];
  }

  if (!Array.isArray(dtos)) {
    return [];
  }

  const result = dtos
    .map((dto) => mapDtoToEdge(dto, edgeType))
    .filter((edge): edge is WorkflowConnection => edge !== null); // Filter out null edges

  return result;
}
